import { create } from "zustand";
import { combine } from "zustand/middleware";

import { getSceneState } from "./sceneStore";
import { createKeyframeActions } from "./fileStore/keyframeActions";
import { createPresetActions } from "./fileStore/presetActions";
import { createProjectActions } from "./fileStore/projectActions";
import { applySceneConfigFromFileMap } from "./fileStore/sceneConfigImport";
import { createXmlActions } from "./fileStore/xmlActions";
import { createXmlGenerationActions } from "./fileStore/xmlGenerationActions";
import { getJointValuesFromMap } from "../utils/jointValues";
import {
  createInitialFileState,
  type FileMap,
  type FileStoreActions,
  type FileStoreState,
} from "./fileStore/types";

const useFileStoreInternal = create<FileStoreState & { actions: FileStoreActions }>(
  combine<FileStoreState, { actions: FileStoreActions }>(
    createInitialFileState(),

    (set, get) => {
      const typedGet = get as unknown as () => FileStoreState & { actions: FileStoreActions };
      return {
        actions: {
          setXmlFile: (file: File | null) => set({ xmlFile: file }),

          setFileMap: (fileMap: FileMap) => set({ fileMap }),

          // Loading state actions
          setUploadingFiles: (uploading: boolean) => set({ uploadingFiles: uploading }),
          setMeshLoadingState: (meshName: string, loading: boolean) =>
            set((state) => ({
              meshLoadingStates: { ...state.meshLoadingStates, [meshName]: loading },
            })),
          setProcessingMeshes: (processing: boolean) => set({ processingMeshes: processing }),
          setParsingXml: (parsing: boolean) => set({ parsingXml: parsing }),

          ...createProjectActions({ set, get: typedGet }),

          ...createXmlGenerationActions({ set, get: typedGet }),

          ...createXmlActions({ set, get: typedGet, applySceneConfigFromFileMap }),
          ...createPresetActions({ set, get: typedGet, applySceneConfigFromFileMap }),
          ...createKeyframeActions({ set, get: typedGet }),

          saveXmlWithUpdates: async (): Promise<void> => {
            const { xmlFile } = get();
            if (!xmlFile) return;

            const sceneState = getSceneState();
            // Direct access to joints map - no tree reconstruction needed

            const text = await xmlFile.text();
            const parser = new window.DOMParser();
            const xmlDoc = parser.parseFromString(text, "application/xml");

            // Update joint positions
            const allJointValues = getJointValuesFromMap(sceneState.joints);
            Object.entries(allJointValues).forEach(([jointName, value]) => {
              const jointElem = xmlDoc.querySelector(`joint[name="${jointName}"]`);
              if (jointElem) {
                jointElem.setAttribute("ref", value.toString());
              }
            });

            // Convert back to string and create download
            const serializer = new XMLSerializer();
            const updatedXml = serializer.serializeToString(xmlDoc);

            const blob = new Blob([updatedXml], { type: "application/xml" });
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = xmlFile.name.replace(".xml", "_updated.xml");
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          },

          saveXmlAs: async (): Promise<void> => {
            const { xmlFile } = get();
            if (!xmlFile) return;

            const text = await xmlFile.text();
            const blob = new Blob([text], { type: "application/xml" });
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = xmlFile.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          },

          // Reset file state
          resetFiles: (): void =>
            set({
              xmlFile: null,
              fileMap: {},
              referencedFiles: [],
              missingFiles: [],
              keyframes: {},
              keyframeDetails: {},
              preservedMjcfSections: {},
              exportWarnings: [],
              pendingMjcfImportWarnings: null,
            }),
        },
      };
    }
  )
);

// Selector hooks for efficient subscriptions
export const useXmlFile = () => useFileStoreInternal((state) => state.xmlFile);
export const useFileMap = () => useFileStoreInternal((state) => state.fileMap);
export const useReferencedFiles = () => useFileStoreInternal((state) => state.referencedFiles);
export const useMissingFiles = () => useFileStoreInternal((state) => state.missingFiles);
export const useKeyframes = () => useFileStoreInternal((state) => state.keyframes);
export const useUploadingFiles = () => useFileStoreInternal((state) => state.uploadingFiles);
export const useMeshLoadingStates = () => useFileStoreInternal((state) => state.meshLoadingStates);
export const useProcessingMeshes = () => useFileStoreInternal((state) => state.processingMeshes);
export const useParsingXml = () => useFileStoreInternal((state) => state.parsingXml);
export const useProjectName = () => useFileStoreInternal((state) => state.projectName);
export const useIsNewProject = () => useFileStoreInternal((state) => state.isNewProject);
export const usePresets = () => useFileStoreInternal((state) => state.presets);
export const useAssetFolders = () => useFileStoreInternal((state) => state.assetFolders);

// Actions hook (doesn't cause re-renders)
export const useFileActions = () => useFileStoreInternal((state) => state.actions);

// Non-hook action accessor for non-component files
export const getFileActions = () => useFileStoreInternal.getState().actions;

// Non-hook state accessor for non-component files
export const getFileState = () => {
  const { actions, ...state } = useFileStoreInternal.getState();
  return state;
};

// Export the main hook following Zustand patterns
export const useFileStore = useFileStoreInternal;
